import { Button, Grid } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { FormAutoCompleteSelectDynamic } from '../../components/hook-form/form-autocomplete-select.dynamic.component';
import { unitsApiKeyStore } from '../../api/units-api-key-store';
import { useFormContext, useWatch } from 'react-hook-form';
import { customersApiKeyStore } from '../../api/customers-api-key-store';
import { officeApiKeyStore } from '../../api/office-api-key-store';
import { projectsApiKeyStore } from '../../api/projects-api-key-store';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import { municipalityApiKeyStore } from '../../api/masters/municipality-api-key-store';
import FormDateField, { DateFormat } from '../../components/hook-form/form-date.field.component';
import dayjs from 'dayjs';
import { cocReportApiKeyStore } from '../../api/coc-report-api-key-store';
import { FormAutoCompleteMultiSelectDynamic } from '../query-builder/hooks-form-querybuilder/form-autocomplete-multi-select.dynamic.component';
import { useSnackbar } from '../../hooks/use-snackbar.hook';

const CocFilter = () => {
  const { setValue, getValues } = useFormContext();
  const { showSnackbar } = useSnackbar();
  const previousValidFromRef = useRef<string>('');
  const previousValidToRef = useRef<string>('');

  const [officeDisabled, setOfficeDisabled] = useState<boolean>(true);
  const [projectDisabled, setProjectDisabled] = useState<boolean>(true);
  const [unitDisabled, setUnitDisabled] = useState<boolean>(true);
  const [disabledToDate, setDisabledToDate] = useState<boolean>(false);
  const [disabledFromData, setDisabledFromDate] = useState<boolean>(false);

  const [fromDate, toDate, customerIDs, officeIDs, projectIDs] = useWatch({
    name: ['fromDate', 'toDate', 'customerIDs', 'officeIDs', 'projectIDs'],
  });

  useEffect(() => {
    setDisabledFromDate(true);
    setDisabledToDate(true);
  }, []);

  useEffect(() => {
    setOfficeDisabled(!(customerIDs?.length > 0));
    setProjectDisabled(!(officeIDs?.length > 0));
    setUnitDisabled(!(projectIDs?.length > 0));
  }, [customerIDs, officeIDs, projectIDs]);

  useEffect(() => {
    if (fromDate) {
      previousValidFromRef.current = fromDate;
    }
    if (toDate) {
      previousValidToRef.current = toDate;
    }
  }, [fromDate, toDate]);

  // const handleCustomerChange = (value: any) => {
  //   setCustomerID(value?.customerID);
  //   setOfficeDisabled(!value);
  //   setProjectDisabled(true);
  //   setUnitDisabled(true);
  //   setValue('office', { officeID: '', officeName: '' });
  //   setValue('project', { projectID: '', projectName: '' });
  //   setValue('unit', { unitID: '', unitName: '' });
  // };

  const handleCustomerChange = (value: any) => {
    setOfficeDisabled(!(value.length > 0));
    setProjectDisabled(true);
    setUnitDisabled(true);
    setValue('officeIDs', null);
    setValue('projectIDs', null);
    setValue('unitIDs', null);
  };


  // const handleOfficeChange = (value: any) => {
  //   setOfficeID(value?.officeID);
  //   setProjectDisabled(!value);
  //   setUnitDisabled(true);
  //   setValue('project', { projectID: '', projectName: '' });
  //   setValue('unit', { unitID: '', unitName: '' });
  // };

  const handleOfficeChange = (value: any) => {
    setProjectDisabled(!(value.length > 0));
    setUnitDisabled(true);
    setValue('projectIDs', null);
    setValue('unitIDs', null);
  };

  const handleProjectChange = (value: any) => {
    setUnitDisabled(!(value.length > 0));
    setValue('unitIDs', null);
  };

  // const handleProjectChange = (value: any) => {
  //   setProjectID(value?.projectID);
  //   setUnitDisabled(!value);
  //   setValue('unit', { unitID: '', unitName: '' });
  // };

  const handleFromDate = (newFrom: any) => {
    setDisabledToDate(!!newFrom);
    const toDate = getValues('toDate');
    if (
      newFrom &&
      toDate &&
      dayjs(newFrom, DateFormat.SIMPLE_DATE).isAfter(dayjs(toDate, DateFormat.SIMPLE_DATE), 'day')
    ) {
      showSnackbar('Date From cannot be greater than Date To.', 'error');
      setValue('fromDate', previousValidFromRef.current);
      return;
    }
    previousValidFromRef.current = newFrom ?? '';
  };

  const handleToDate = (newTo: any) => {
    const fromDate = getValues('fromDate');
    if (
      newTo &&
      fromDate &&
      dayjs(newTo, DateFormat.SIMPLE_DATE).isBefore(dayjs(fromDate, DateFormat.SIMPLE_DATE), 'day')
    ) {
      showSnackbar('Date To cannot be less than Date From.', 'error');
      setValue('toDate', previousValidToRef.current);
      return;
    }
    previousValidToRef.current = newTo ?? '';
  };

  return (
    <div>
      <Grid container spacing={1}>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Grid container spacing={1}>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="customerIDs"
                label="Customers"
                onChange={handleCustomerChange}
                getQueryKeyConfig={() => customersApiKeyStore.getCustomers()}
                optionsLabelKey="customerName"
                optionsValueKey="customerID"
              />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="officeIDs"
                label="Offices"
                watchKeys={['customerIDs']}
                onChange={handleOfficeChange}
                getQueryKeyConfig={(inputData: any) =>
                  officeApiKeyStore.getCustomersOffices(inputData.customerIDs?.map((o: any) => o.customerID))
                }
                optionsLabelKey="officeName"
                optionsValueKey="officeID"
                disabled={officeDisabled}
              />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="projectIDs"
                label="Projects"
                watchKeys={['officeIDs']}
                onChange={handleProjectChange}
                getQueryKeyConfig={(inputData: any) =>
                  projectsApiKeyStore.getOfficesProjects(inputData.officeIDs?.map((o: any) => o.officeID))
                }
                optionsLabelKey="projectName"
                optionsValueKey="projectID"
                disabled={projectDisabled}
              />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="unitIDs"
                label="Lot/Unit"
                watchKeys={['projectIDs']}
                getQueryKeyConfig={(inputData: any) =>
                  unitsApiKeyStore.getProjectsUnits(inputData.projectIDs?.map((o: any) => o.projectID))
                }
                optionsLabelKey="unitName"
                optionsValueKey="unitID"
                disabled={unitDisabled}
              />
            </Grid>
            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="municipalityIDs"
                label="Municipalities"
                getQueryKeyConfig={() => municipalityApiKeyStore.getMunicipalities()}
                optionsLabelKey="muncipalityName"
                optionsValueKey="municipalityID"
              />
            </Grid>

            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="cocStatus"
                label="Status"
                getQueryKeyConfig={() => cocReportApiKeyStore.getCocReportStatus()}
                optionsLabelKey="coCStatus"
                optionsValueKey="coCStatusValue"
              />
            </Grid>
            {/* <Grid item xl={3} lg={3} sm={4} xs={6}>
              <FormAutoCompleteMultiSelectDynamic
                name="status"
                label="Status"
                // onChange={() => {
                //   hanldeChangeWorkOrderID();
                // }}
                // disabled={!getValues('customer')?.customerID}
                getQueryKeyConfig={() => workOrdersApiKeyStore.getWorkOrderStatuses('inspection')}
                optionsValueKey="workOrderStatusID"
                optionsLabelKey="workOrderStatus"
              />
            </Grid> */}
            <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
              <FormDateField
                label="Date From"
                name="fromDate"
                onChange={handleFromDate}
                disabled={!disabledFromData}
                format={DateFormat.SIMPLE_DATE}
                dateLimits={{
                  maxDateEnabled: true,
                  maxDateValue: 'toDate',
                }}
              />
            </Grid>
            <Grid item xl={1.5} lg={1.5} sm={4} xs={6}>
              <FormDateField
                label="Date To"
                name="toDate"
                onChange={handleToDate}
                disabled={!disabledToDate}
                format={DateFormat.SIMPLE_DATE}
                dateLimits={{
                  minDateEnabled: true,
                  minDateValue: 'fromDate',
                }}
              />
            </Grid>
            {/* <Grid item xl={3} lg={3} sm={4} xs={6}>
              <TextField name="searchKey" label="Search" />
            </Grid> */}

            <Grid item xl={3} lg={3} sm={4} xs={6}>
              <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                <Button type="submit" title="Filter" variant="contained" startIcon={<FilterAltOutlinedIcon />}>
                  Filter
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default CocFilter;
